import { smoothPath } from '../../school_panel/components/charts/svgPath';

export type GrowthPoint = { label: string; value: number; title: string };

const fmtAxis = (x: number) => (Math.abs(x) >= 1000 ? `${(x / 1000).toFixed(1)}k` : String(Math.round(x)));

/** Round the y-range outwards to a unit one decade below the data (the artboard uses 1000 for ~30k). */
function domain(vals: number[]): [number, number] {
  const max = Math.max(...vals, 1);
  const unit = Math.max(1, 10 ** Math.floor(Math.log10(max)) / 10);
  const lo = Math.max(0, Math.floor((Math.min(...vals) * 0.9) / unit) * unit);
  let hi = Math.ceil((max * 1.05) / unit) * unit;
  if (hi <= lo) hi = lo + unit;
  return [lo, hi];
}

/** Design AdminDashboard growth chart: smoothed area + line, y grid, last-point ring and a navy value tag. */
export default function GrowthChart({ points, ariaLabel }: { points: GrowthPoint[]; ariaLabel: string }) {
  const vals = points.map((p) => p.value);
  const [lo, hi] = domain(vals);
  const X = (i: number) => (points.length === 1 ? 405 : 70 + i * (670 / (points.length - 1)));
  const Y = (v: number) => 210 - ((v - lo) / (hi - lo)) * 190;
  const pts = vals.map((v, i) => [X(i), Y(v)] as [number, number]);
  const line = smoothPath(pts);
  const last = pts[pts.length - 1];
  const area = `${line} L${last[0]},210 L${pts[0][0]},210 Z`;
  const grid = [0, 1, 2, 3].map((k) => { const v = lo + ((hi - lo) * k) / 3; return { y: Y(v), label: fmtAxis(v) }; });
  const step = points.length > 6 ? 2 : 1;
  return (
    <svg viewBox="0 0 760 250" className="block h-auto w-full" role="img" aria-label={ariaLabel}>
      {grid.map((g) => (
        <g key={g.label + g.y}>
          <line x1={56} x2={750} y1={g.y} y2={g.y} className="stroke-line" />
          <text x={46} y={g.y + 4} textAnchor="end" className="fill-muted font-mono text-[11px]">{g.label}</text>
        </g>
      ))}
      <path d={area} className="fill-slate" fillOpacity={0.12} />
      <path d={line} fill="none" className="stroke-slate-ink" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
      {pts.map(([x, y], i) => (
        <circle key={points[i].title} cx={x} cy={y} r={9} fill="transparent"><title>{points[i].title}</title></circle>
      ))}
      <circle cx={last[0]} cy={last[1]} r={7} className="fill-surface stroke-accent" strokeWidth={4} />
      <rect x={last[0] - 100} y={last[1] - 14} width={84} height={28} rx={8} className="fill-navy" />
      <text x={last[0] - 58} y={last[1] + 5} textAnchor="middle" className="fill-white font-mono text-[13px] font-semibold">
        {vals[vals.length - 1].toLocaleString('en-US')}
      </text>
      {points.map((p, i) => i % step === 0 && (
        <text key={p.title} x={X(i)} y={240} textAnchor="middle" className="fill-muted font-mono text-[11px]">{p.label}</text>
      ))}
    </svg>
  );
}
